/**
 * Model Display Configuration
 *
 * Single source of truth for all model UX data: display names, capability groups,
 * tooltip content, subtitle tags, and search text. The backend model registries
 * provide technical properties (sizes, settings, costs); this file provides the
 * human-facing presentation layer.
 *
 * Adding a new model? Just add an entry here — search works automatically from
 * tooltip content.
 */

// ─── Capability Groups ────────────────────────────────────────────────────────

export type ImageCapabilityGroup = 'text-to-image' | 'image-editing' | 'multi-image' | 'character-style';
export type VideoCapabilityGroup = 'text-to-video' | 'image-to-video' | 'multi-ref-special';
export type TextCapabilityGroup = 'text-generation';
export type CapabilityGroup = ImageCapabilityGroup | VideoCapabilityGroup | TextCapabilityGroup;

export const IMAGE_GROUP_ORDER: ImageCapabilityGroup[] = [
  'text-to-image',
  'image-editing',
  'multi-image',
  'character-style',
];

export const VIDEO_GROUP_ORDER: VideoCapabilityGroup[] = [
  'text-to-video',
  'image-to-video',
  'multi-ref-special',
];

export const TEXT_GROUP_ORDER: TextCapabilityGroup[] = ['text-generation'];

export const GROUP_LABELS: Record<CapabilityGroup, { emoji: string; label: string }> = {
  'text-to-image': { emoji: '🖼️', label: 'Text to Image' },
  'image-editing': { emoji: '✏️', label: 'Image Editing' },
  'multi-image': { emoji: '🔀', label: 'Multi-Image Editing' },
  'character-style': { emoji: '👤', label: 'Character & Style' },
  'text-to-video': { emoji: '🎬', label: 'Text to Video' },
  'image-to-video': { emoji: '📷', label: 'Image to Video' },
  'multi-ref-special': { emoji: '🔀', label: 'Multi-Ref & Special' },
  'text-generation': { emoji: '💬', label: 'Text Generation' },
};

// ─── Tooltip Data ─────────────────────────────────────────────────────────────

export interface ModelTooltip {
  description: string;
  capabilities: string;
  pricing: string;
  bestFor: string;
}

// ─── Display Config Entry ─────────────────────────────────────────────────────

export interface ModelDisplayEntry {
  displayName: string;
  technicalName: string;
  group: CapabilityGroup;
  subtitleTags: string[];
  tooltip: ModelTooltip;
}

// ─── Search Text Builder ──────────────────────────────────────────────────────

export function buildSearchText(entry: ModelDisplayEntry): string {
  return [
    entry.displayName,
    entry.technicalName,
    entry.tooltip.description,
    entry.tooltip.capabilities,
    entry.tooltip.bestFor,
  ]
    .join(' ')
    .toLowerCase();
}

// ─── Credit Tier Colors ───────────────────────────────────────────────────────

export type CreditTier = 'budget' | 'standard' | 'premium' | 'ultra' | 'free';

export function getCreditTier(credits: number, isFree?: boolean): CreditTier {
  if (isFree) return 'free';
  if (credits <= 5) return 'budget';
  if (credits <= 25) return 'standard';
  if (credits <= 49) return 'premium';
  return 'ultra';
}

export const CREDIT_TIER_STYLES: Record<CreditTier, { bg: string; text: string }> = {
  free: { bg: 'bg-green-50', text: 'text-green-600' },
  budget: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  standard: { bg: 'bg-gray-100', text: 'text-gray-600' },
  premium: { bg: 'bg-warning-50', text: 'text-warning-700' },
  ultra: { bg: 'bg-red-50', text: 'text-red-600' },
};

// ─── Image Model Display Config ───────────────────────────────────────────────

export const IMAGE_MODEL_DISPLAY: Record<string, ModelDisplayEntry> = {
  // ── Text to Image ──────────────────────────────────────────────────────────
  'flux-schnell': {
    displayName: 'FLUX Schnell',
    technicalName: 'FLUX Schnell',
    group: 'text-to-image',
    subtitleTags: ['Fastest', 'Budget'],
    tooltip: {
      description: 'Fastest FLUX model, ultra-cheap',
      capabilities: '✅ Text to Image',
      pricing: '1 cr/image',
      bestFor: 'Rapid prototyping, exploring ideas, maximum volume',
    },
  },
  'gpt-image-1.5-low': {
    displayName: 'GPT Image 1.5 Low',
    technicalName: 'GPT Image 1.5 Low',
    group: 'text-to-image',
    subtitleTags: ['Fast drafts', 'Transparent BG'],
    tooltip: {
      description: 'Fast cheap iterations from OpenAI',
      capabilities: '✅ Text to Image, ⚙️ Background (Auto/Transparent/Opaque)',
      pricing: '3 cr (1024²), 4 cr (1536×1024)',
      bestFor: 'Drafting, exploring ideas, transparent backgrounds',
    },
  },
  'gpt-image-2-low': {
    displayName: 'GPT Image 2 Low',
    technicalName: 'GPT Image 2 Low',
    group: 'text-to-image',
    subtitleTags: ['Fast drafts', '1K–4K'],
    tooltip: {
      description: 'Fast cheap iterations from OpenAI GPT Image 2',
      capabilities: '✅ Text to Image, ✅ Size tiers (1K/2K/4K), ✅ 9 aspect ratios',
      pricing: '3 cr (1K), 7 cr (2K), 10 cr (4K)',
      bestFor: 'Drafting, exploring ideas, multi-resolution iterations',
    },
  },
  'gpt-image-2-medium': {
    displayName: 'GPT Image 2 Medium',
    technicalName: 'GPT Image 2 Medium',
    group: 'text-to-image',
    subtitleTags: ['Balanced', '1K–4K'],
    tooltip: {
      description: 'Balanced quality from OpenAI GPT Image 2',
      capabilities: '✅ Text to Image, ✅ Size tiers (1K/2K/4K), ✅ 9 aspect ratios',
      pricing: '23 cr (1K), 27 cr (2K), 43 cr (4K)',
      bestFor: 'Everyday generation across a range of resolutions',
    },
  },
  'gpt-image-2-high': {
    displayName: 'GPT Image 2 High',
    technicalName: 'GPT Image 2 High',
    group: 'text-to-image',
    subtitleTags: ['Maximum fidelity', '1K–4K'],
    tooltip: {
      description: 'Maximum fidelity from OpenAI GPT Image 2',
      capabilities: '✅ Text to Image, ✅ Size tiers (1K/2K/4K), ✅ 9 aspect ratios',
      pricing: '83 cr (1K), 93 cr (2K), 150 cr (4K)',
      bestFor: 'Final-quality renders, hero images, print-ready output',
    },
  },
  'minimax-image-01': {
    displayName: 'MiniMax Image',
    technicalName: 'MiniMax Image',
    group: 'text-to-image',
    subtitleTags: ['Fast', 'Budget'],
    tooltip: {
      description: 'Fast text-to-image model',
      capabilities: '✅ Text to Image, ⚙️ Prompt Optimizer',
      pricing: '3 cr/image',
      bestFor: 'Budget generation, quick tests',
    },
  },
  'luma-photon': {
    displayName: 'Luma Photon',
    technicalName: 'Luma Photon',
    group: 'text-to-image',
    subtitleTags: ['Creative', 'Artistic'],
    tooltip: {
      description: 'Creative text-to-image generation',
      capabilities: '✅ Text to Image',
      pricing: '6 cr/image',
      bestFor: 'Creative/artistic generation',
    },
  },
  'flux-dev': {
    displayName: 'FLUX Dev',
    technicalName: 'FLUX Dev',
    group: 'text-to-image',
    subtitleTags: ['High quality', 'Tunable'],
    tooltip: {
      description: 'High-quality FLUX model with tunable steps',
      capabilities: '✅ Text to Image, ⚙️ Inference Steps, ⚙️ Guidance Scale',
      pricing: '8 cr/image',
      bestFor: 'Detailed generation when budget allows more control',
    },
  },
  'ideogram-v3-turbo': {
    displayName: 'Ideogram V3 Turbo',
    technicalName: 'Ideogram V3 Turbo',
    group: 'text-to-image',
    subtitleTags: ['Fast', 'Best for text in images'],
    tooltip: {
      description: 'Fast generation — best for text/typography in images',
      capabilities: '✅ Text to Image, ✅ Optional refs, ⚙️ Style (Auto/General/Realistic/Design), ⚙️ Expand Prompt',
      pricing: '10 cr/image',
      bestFor: 'Posters, logos, any image with readable text',
    },
  },
  'seedream-v4': {
    displayName: 'Seedream V4',
    technicalName: 'Seedream V4',
    group: 'text-to-image',
    subtitleTags: ['Versatile', 'Up to 10 refs'],
    tooltip: {
      description: 'Unified generation and editing with reference support',
      capabilities: '✅ Text to Image, ✅ Edit (up to 10 refs), ⚙️ Enhance Prompt',
      pricing: '10 cr (standard), 20 cr (2K/4K)',
      bestFor: 'Versatile budget generation and editing',
    },
  },
  'gpt-image-1.5-medium': {
    displayName: 'GPT Image 1.5 Medium',
    technicalName: 'GPT Image 1.5 Medium',
    group: 'text-to-image',
    subtitleTags: ['Balanced quality'],
    tooltip: {
      description: 'Balanced quality from OpenAI',
      capabilities: '✅ Text to Image, ⚙️ Background',
      pricing: '11 cr (1024²), 17 cr (1536×1024)',
      bestFor: 'Everyday generation, good quality-to-cost',
    },
  },
  'gemini-2.5-flash-image': {
    displayName: 'Nano Banana',
    technicalName: 'Gemini 2.5 Flash',
    group: 'text-to-image',
    subtitleTags: ['All-rounder', 'Up to 14 refs'],
    tooltip: {
      description: 'Fast, affordable image generation with aspect ratio control. Great for quick iterations and general purpose creative work.',
      capabilities: '✅ Text to Image, ✅ Edit (up to 14 refs), ✅ Custom dimensions',
      pricing: '13 cr/image',
      bestFor: 'Quick iterations, general purpose, budget-friendly bulk generation',
    },
  },
  'recraft-v3': {
    displayName: 'Recraft V3',
    technicalName: 'Recraft V3',
    group: 'text-to-image',
    subtitleTags: ['25+ styles', 'Illustration & Realistic'],
    tooltip: {
      description: 'Stylized generation with 25+ realistic and illustration styles',
      capabilities: '✅ Text to Image, ⚙️ 25+ Style options',
      pricing: '13 cr/image',
      bestFor: 'Illustrations, design assets, style-specific work',
    },
  },
  'seedream-v4.5': {
    displayName: 'Seedream 4.5',
    technicalName: 'Seedream 4.5',
    group: 'text-to-image',
    subtitleTags: ['Good all-rounder', 'Up to 10 refs'],
    tooltip: {
      description: 'ByteDance next-gen image generation and editing',
      capabilities: '✅ Text to Image, ✅ Edit (up to 10 refs)',
      pricing: '13 cr/image',
      bestFor: 'Good all-rounder, balanced quality and price',
    },
  },
  'flux-2-flex': {
    displayName: 'FLUX 2 Flex',
    technicalName: 'FLUX 2 Flex',
    group: 'text-to-image',
    subtitleTags: ['Versatile', 'Up to 10 refs'],
    tooltip: {
      description: 'Fast high-quality generation with optional multi-image editing',
      capabilities: '✅ Text to Image, ✅ Edit (up to 10 refs), ⚙️ Inference Steps, ⚙️ Guidance, ⚙️ Expand Prompt',
      pricing: '17 cr (1MP), 33 cr (HD 16:9), 38 cr (Square HD)',
      bestFor: 'Versatile — works as both text-to-image and image editor',
    },
  },
  'ideogram-v3': {
    displayName: 'Ideogram V3',
    technicalName: 'Ideogram V3',
    group: 'text-to-image',
    subtitleTags: ['Balanced', 'Typography'],
    tooltip: {
      description: 'Balanced quality with excellent typography',
      capabilities: '✅ Text to Image, ✅ Optional refs, ⚙️ Style, ⚙️ Expand Prompt',
      pricing: '20 cr/image',
      bestFor: 'Typography-heavy designs at higher quality',
    },
  },
  'flux-1.1-pro-ultra': {
    displayName: 'FLUX 1.1 Pro Ultra',
    technicalName: 'FLUX 1.1 Pro Ultra',
    group: 'text-to-image',
    subtitleTags: ['Ultra quality', 'Photorealistic'],
    tooltip: {
      description: 'Premium FLUX with optional single-image guidance',
      capabilities: '✅ Text to Image, ✅ Optional 1 ref image, ⚙️ Enhance Prompt, ⚙️ Raw mode',
      pricing: '20 cr/image',
      bestFor: 'Ultra-quality hero images, photorealistic output',
    },
  },
  'gemini-3.1-flash-image-preview': {
    displayName: 'Nano Banana 2',
    technicalName: 'Gemini 3.1 Flash',
    group: 'text-to-image',
    subtitleTags: ['Size tiers', 'Up to 14 refs'],
    tooltip: {
      description: 'Next-gen image generation with size tiers and reference images',
      capabilities: '✅ Text to Image, ✅ Edit (up to 14 refs), ✅ Custom dimensions, ✅ Size tiers (512px–4K)',
      pricing: '15 cr (512px), 22 cr (1K), 34 cr (2K), 50 cr (4K)',
      bestFor: 'Higher resolution output, size control, balanced price-to-quality',
    },
  },
  'ideogram-v3-quality': {
    displayName: 'Ideogram V3 Quality',
    technicalName: 'Ideogram V3 Quality',
    group: 'text-to-image',
    subtitleTags: ['Premium', 'Best typography'],
    tooltip: {
      description: 'Maximum quality, best text rendering',
      capabilities: '✅ Text to Image, ✅ Optional refs, ⚙️ Style, ⚙️ Expand Prompt',
      pricing: '30 cr/image',
      bestFor: 'Final renders with text, professional typography work',
    },
  },
  'gpt-image-1.5-high': {
    displayName: 'GPT Image 1.5 High',
    technicalName: 'GPT Image 1.5 High',
    group: 'text-to-image',
    subtitleTags: ['Maximum fidelity'],
    tooltip: {
      description: 'Maximum fidelity from OpenAI',
      capabilities: '✅ Text to Image, ⚙️ Background',
      pricing: '44 cr (1024²), 66 cr (1536×1024)',
      bestFor: 'Final-quality output, detailed scenes',
    },
  },
  'gemini-3-pro-image-preview': {
    displayName: 'Nano Banana Pro',
    technicalName: 'Gemini 3 Pro',
    group: 'text-to-image',
    subtitleTags: ['Highest quality', 'Up to 14 refs'],
    tooltip: {
      description: 'Highest quality generation with size control',
      capabilities: '✅ Text to Image, ✅ Edit (up to 14 refs), ✅ Custom dimensions, ✅ Up to 4K',
      pricing: '45 cr (1K/2K), 80 cr (4K)',
      bestFor: 'Final renders, hero images, maximum quality output',
    },
  },

  // ── Image Editing (single ref) ────────────────────────────────────────────
  'flux-kontext-pro': {
    displayName: 'FLUX Kontext Pro',
    technicalName: 'FLUX Kontext Pro',
    group: 'image-editing',
    subtitleTags: ['Contextual editing', 'Guidance control'],
    tooltip: {
      description: 'Single-image editing with contextual understanding',
      capabilities: '✅ Image Editing (1 ref required), ⚙️ Guidance Scale',
      pricing: '13 cr/image',
      bestFor: 'Quick image modifications, style changes, object manipulation',
    },
  },
  'recraft-v3-edit': {
    displayName: 'Recraft V3 Edit',
    technicalName: 'Recraft V3 Edit',
    group: 'image-editing',
    subtitleTags: ['Style transfer', '25+ styles', 'Strength slider'],
    tooltip: {
      description: 'Transform images with style control and strength',
      capabilities: '✅ Image Editing (1 ref required), ⚙️ 25+ Styles, ⚙️ Strength slider',
      pricing: '13 cr/image',
      bestFor: 'Style transfer on existing images',
    },
  },
  'flux-kontext-max': {
    displayName: 'FLUX Kontext Max',
    technicalName: 'FLUX Kontext Max',
    group: 'image-editing',
    subtitleTags: ['Premium editing', 'Higher fidelity'],
    tooltip: {
      description: 'Premium single-image editing',
      capabilities: '✅ Image Editing (1 ref required), ⚙️ Guidance, ⚙️ Safety',
      pricing: '27 cr/image',
      bestFor: 'High-fidelity image editing when quality matters most',
    },
  },

  // ── Multi-Image Editing ───────────────────────────────────────────────────
  'gpt-image-1.5-edit-low': {
    displayName: 'GPT Image 1.5 Edit Low',
    technicalName: 'GPT Image 1.5 Edit Low',
    group: 'multi-image',
    subtitleTags: ['Fast drafts', 'Up to 10 refs'],
    tooltip: {
      description: 'Quick edits for iterating on compositions',
      capabilities: '✅ Image Editing (1-10 refs required), ⚙️ Background, ⚙️ Input Fidelity',
      pricing: '3 cr (1024²), 4 cr (1536×1024)',
      bestFor: 'Quick edit iterations, composition testing',
    },
  },
  'gpt-image-2-edit-low': {
    displayName: 'GPT Image 2 Edit Low',
    technicalName: 'GPT Image 2 Edit Low',
    group: 'multi-image',
    subtitleTags: ['Fast drafts', 'Up to 10 refs', '1K–4K'],
    tooltip: {
      description: 'Quick OpenAI GPT Image 2 edits across multiple references',
      capabilities: '✅ Image Editing (1-10 refs required), ✅ Size tiers (1K/2K/4K), ✅ 9 aspect ratios',
      pricing: '3 cr (1K), 7 cr (2K), 10 cr (4K)',
      bestFor: 'Quick edit iterations, composition testing at any resolution',
    },
  },
  'gpt-image-2-edit-medium': {
    displayName: 'GPT Image 2 Edit Medium',
    technicalName: 'GPT Image 2 Edit Medium',
    group: 'multi-image',
    subtitleTags: ['Balanced', 'Up to 10 refs', '1K–4K'],
    tooltip: {
      description: 'Balanced OpenAI GPT Image 2 multi-image editing',
      capabilities: '✅ Image Editing (1-10 refs required), ✅ Size tiers (1K/2K/4K), ✅ 9 aspect ratios',
      pricing: '23 cr (1K), 27 cr (2K), 43 cr (4K)',
      bestFor: 'General multi-reference editing across resolutions',
    },
  },
  'gpt-image-2-edit-high': {
    displayName: 'GPT Image 2 Edit High',
    technicalName: 'GPT Image 2 Edit High',
    group: 'multi-image',
    subtitleTags: ['Maximum fidelity', 'Up to 10 refs', '1K–4K'],
    tooltip: {
      description: 'High-fidelity OpenAI GPT Image 2 editing for final output',
      capabilities: '✅ Image Editing (1-10 refs required), ✅ Size tiers (1K/2K/4K), ✅ 9 aspect ratios',
      pricing: '83 cr (1K), 93 cr (2K), 150 cr (4K)',
      bestFor: 'Final-quality multi-reference image edits',
    },
  },
  'gpt-image-1.5-edit-medium': {
    displayName: 'GPT Image 1.5 Edit Medium',
    technicalName: 'GPT Image 1.5 Edit Medium',
    group: 'multi-image',
    subtitleTags: ['Balanced', 'Up to 10 refs'],
    tooltip: {
      description: 'Balanced editing quality',
      capabilities: '✅ Image Editing (1-10 refs required), ⚙️ Background, ⚙️ Input Fidelity',
      pricing: '11 cr (1024²), 17 cr (1536×1024)',
      bestFor: 'General image editing',
    },
  },
  'seedream-v5-lite-edit': {
    displayName: 'Seedream 5 Lite Edit',
    technicalName: 'Seedream 5 Lite Edit',
    group: 'multi-image',
    subtitleTags: ['Fast', 'Up to 10 refs'],
    tooltip: {
      description: 'Fast multi-image edit from ByteDance',
      capabilities: '✅ Image Editing (ref required, up to 10)',
      pricing: '12 cr/image',
      bestFor: 'Quick multi-reference editing',
    },
  },
  'seedream-v4.5-edit': {
    displayName: 'Seedream 4.5 Edit',
    technicalName: 'Seedream 4.5 Edit',
    group: 'multi-image',
    subtitleTags: ['Stylization', 'Up to 10 refs'],
    tooltip: {
      description: 'ByteDance image editing and stylization',
      capabilities: '✅ Image Editing (ref required, up to 10)',
      pricing: '13 cr/image',
      bestFor: 'Style transfer, image modification',
    },
  },
  'flux-kontext-pro-multi': {
    displayName: 'FLUX Kontext Pro Multi',
    technicalName: 'FLUX Kontext Pro Multi',
    group: 'multi-image',
    subtitleTags: ['Combine & transform', '2-4 refs'],
    tooltip: {
      description: 'Multi-image editing — combine and transform references',
      capabilities: '✅ Multi-Image Editing (2-4 refs required), ⚙️ Guidance',
      pricing: '13 cr/image',
      bestFor: 'Combining elements from multiple images, character sheets',
    },
  },
  'qwen-image-edit-plus': {
    displayName: 'Qwen Image Edit Plus',
    technicalName: 'Qwen Image Edit Plus',
    group: 'multi-image',
    subtitleTags: ['Strong text editing', 'Up to 10 refs'],
    tooltip: {
      description: 'Multi-image edit with strong text editing support',
      capabilities: '✅ Image Editing (ref required, up to 10), ⚙️ Inference Steps, ⚙️ Guidance',
      pricing: '23 cr/image',
      bestFor: 'Editing text within images, multi-ref editing',
    },
  },
  'flux-kontext-max-multi': {
    displayName: 'FLUX Kontext Max Multi',
    technicalName: 'FLUX Kontext Max Multi',
    group: 'multi-image',
    subtitleTags: ['Premium compositing', '2-4 refs'],
    tooltip: {
      description: 'Premium multi-image editing',
      capabilities: '✅ Multi-Image Editing (2-4 refs required), ⚙️ Guidance, ⚙️ Safety',
      pricing: '27 cr/image',
      bestFor: 'High-fidelity multi-image compositing',
    },
  },
  'gpt-image-1.5-edit-high': {
    displayName: 'GPT Image 1.5 Edit High',
    technicalName: 'GPT Image 1.5 Edit High',
    group: 'multi-image',
    subtitleTags: ['Maximum fidelity', 'Up to 10 refs'],
    tooltip: {
      description: 'High-fidelity editing',
      capabilities: '✅ Image Editing (1-10 refs required), ⚙️ Background, ⚙️ Input Fidelity',
      pricing: '44 cr (1024²), 66 cr (1536×1024)',
      bestFor: 'Final-quality image edits',
    },
  },

  // ── Character & Style ─────────────────────────────────────────────────────
  'ideogram-v3-character': {
    displayName: 'Ideogram V3 Character',
    technicalName: 'Ideogram V3 Character',
    group: 'character-style',
    subtitleTags: ['Character consistency across scenes'],
    tooltip: {
      description: 'Character-consistent generation using a reference',
      capabilities: '✅ Character Consistency (1 ref required), ⚙️ Style (Auto/Realistic/Fiction)',
      pricing: '67 cr/image',
      bestFor: 'Character consistency across scenes, character turnarounds',
    },
  },
};

// ─── Video Model Display Config ───────────────────────────────────────────────

export const VIDEO_MODEL_DISPLAY: Record<string, ModelDisplayEntry> = {
  // ── Text to Video ─────────────────────────────────────────────────────────
  'seedance-1.5-t2v': {
    displayName: 'Seedance 1.5',
    technicalName: 'Seedance 1.5',
    group: 'text-to-video',
    subtitleTags: ['Affordable', 'Audio toggle'],
    tooltip: {
      description: 'Affordable text-to-video with optional audio',
      capabilities: '✅ Text to Video, 🔊 Audio toggle, 5-10s',
      pricing: '86 cr (5s audio) / 43 cr (silent)',
      bestFor: 'Budget video generation, quick tests',
    },
  },
  'ltx-2-t2v': {
    displayName: 'LTX Video 2.0',
    technicalName: 'LTX Video 2.0',
    group: 'text-to-video',
    subtitleTags: ['Fast', 'Affordable', 'Audio'],
    tooltip: {
      description: 'Fast and affordable text-to-video with audio',
      capabilities: '✅ Text to Video, 🔊 Audio, 720p-1080p, 5s',
      pricing: '67 cr (720p) / 150 cr (1080p)',
      bestFor: 'Fast video drafts with audio',
    },
  },
  'hailuo-02-t2v': {
    displayName: 'Hailuo-02',
    technicalName: 'Hailuo-02',
    group: 'text-to-video',
    subtitleTags: ['768p', '6s'],
    tooltip: {
      description: 'Simple text-to-video generation',
      capabilities: '✅ Text to Video, 768p, 6s',
      pricing: '90 cr',
      bestFor: 'Simple video generation',
    },
  },
  'kling-2.5-turbo-t2v': {
    displayName: 'Kling 2.5 Turbo',
    technicalName: 'Kling 2.5 Turbo',
    group: 'text-to-video',
    subtitleTags: ['Fast', '1080p'],
    tooltip: {
      description: 'Fast 1080p text-to-video',
      capabilities: '✅ Text to Video, 1080p, 5-10s',
      pricing: '117 cr (5s)',
      bestFor: 'Fast 1080p video on a budget',
    },
  },
  'sora-2-t2v': {
    displayName: 'Sora 2',
    technicalName: 'Sora 2',
    group: 'text-to-video',
    subtitleTags: ['Audio always on'],
    tooltip: {
      description: 'OpenAI video generation with built-in audio',
      capabilities: '✅ Text to Video, 🔊 Audio (always on), 4-10s',
      pricing: '133 cr (4s)',
      bestFor: 'Quality video with audio from OpenAI',
    },
  },
  'veo-3.1-fast': {
    displayName: 'Veo 3.1 Fast',
    technicalName: 'Veo 3.1 Fast',
    group: 'text-to-video',
    subtitleTags: ['Fast', 'Audio toggle'],
    tooltip: {
      description: 'Fast Google video generation with optional audio',
      capabilities: '✅ Text to Video, 🔊 Audio toggle, 4-8s',
      pricing: '133 cr (4s silent) / 200 cr (audio)',
      bestFor: 'Fast Google-quality video',
    },
  },
  'kling-3.0-std-t2v': {
    displayName: 'Kling 3.0 Standard',
    technicalName: 'Kling 3.0 Standard',
    group: 'text-to-video',
    subtitleTags: ['Cinematic', 'Audio', '1080p'],
    tooltip: {
      description: 'Cinematic 1080p video with optional audio',
      capabilities: '✅ Text to Video, 🔊 Audio toggle, 1080p, 5-10s',
      pricing: '280 cr (5s silent) / 420 cr (audio)',
      bestFor: 'Cinematic quality video with audio',
    },
  },
  'kling-o3-std-t2v': {
    displayName: 'Kling O3 Standard',
    technicalName: 'Kling O3 Standard',
    group: 'text-to-video',
    subtitleTags: ['Multi-shot', 'Audio', '1080p'],
    tooltip: {
      description: 'Latest Kling model with multi-shot support',
      capabilities: '✅ Text to Video, 🔊 Audio toggle, 1080p, 5-10s',
      pricing: '280 cr (5s silent) / 373 cr (audio)',
      bestFor: 'Latest Kling quality video',
    },
  },
  'kling-3.0-pro-t2v': {
    displayName: 'Kling 3.0 Pro',
    technicalName: 'Kling 3.0 Pro',
    group: 'text-to-video',
    subtitleTags: ['Premium', 'Audio', '1080p'],
    tooltip: {
      description: 'Premium 1080p video with audio',
      capabilities: '✅ Text to Video, 🔊 Audio toggle, 1080p, 5-10s',
      pricing: '373 cr (5s silent) / 560 cr (audio)',
      bestFor: 'Premium video generation',
    },
  },
  'kling-o3-pro-t2v': {
    displayName: 'Kling O3 Pro',
    technicalName: 'Kling O3 Pro',
    group: 'text-to-video',
    subtitleTags: ['Top tier', 'Audio', '1080p'],
    tooltip: {
      description: 'Top tier Kling video generation',
      capabilities: '✅ Text to Video, 🔊 Audio toggle, 1080p, 5-10s',
      pricing: '373 cr (5s silent) / 467 cr (audio)',
      bestFor: 'Best possible Kling video quality',
    },
  },
  // Admin-only T2V
  'veo-3.1': {
    displayName: 'Veo 3.1',
    technicalName: 'Veo 3.1',
    group: 'text-to-video',
    subtitleTags: ['Audio', 'Admin only'],
    tooltip: {
      description: 'Google premium video generation with audio',
      capabilities: '✅ Text to Video, 🔊 Audio toggle, 4-8s',
      pricing: '267 cr (4s silent) / 533 cr (audio)',
      bestFor: 'Highest quality Google video',
    },
  },
  'sora-2-pro-t2v': {
    displayName: 'Sora 2 Pro',
    technicalName: 'Sora 2 Pro',
    group: 'text-to-video',
    subtitleTags: ['1080p', 'Admin only'],
    tooltip: {
      description: 'Premium OpenAI video generation',
      capabilities: '✅ Text to Video, 🔊 Audio, 1080p, 4-20s',
      pricing: '400 cr (4s 720p)',
      bestFor: 'Premium OpenAI video',
    },
  },
  'seedance-1.0-fast-t2v': {
    displayName: 'Seedance 1.0 Fast',
    technicalName: 'Seedance 1.0 Fast',
    group: 'text-to-video',
    subtitleTags: ['Fast', 'Budget', 'Admin only'],
    tooltip: {
      description: 'Fast budget text-to-video',
      capabilities: '✅ Text to Video, 5s',
      pricing: '36 cr (5s)',
      bestFor: 'Fast budget video',
    },
  },
  'hailuo-02-pro-t2v': {
    displayName: 'Hailuo-02 Pro',
    technicalName: 'Hailuo-02 Pro',
    group: 'text-to-video',
    subtitleTags: ['1080p', 'Admin only'],
    tooltip: {
      description: '1080p Hailuo video generation',
      capabilities: '✅ Text to Video, 1080p, 6s',
      pricing: '160 cr (6s)',
      bestFor: 'Higher quality Hailuo video',
    },
  },

  // ── Image to Video ────────────────────────────────────────────────────────
  'seedance-1.5-i2v': {
    displayName: 'Seedance 1.5 I2V',
    technicalName: 'Seedance 1.5 I2V',
    group: 'image-to-video',
    subtitleTags: ['Affordable', 'Audio toggle'],
    tooltip: {
      description: 'Affordable image animation with optional audio',
      capabilities: '✅ Image to Video (1 ref required), 🔊 Audio toggle, 5-10s',
      pricing: '86 cr (5s audio) / 43 cr (silent)',
      bestFor: 'Budget image animation',
    },
  },
  'ltx-2-i2v': {
    displayName: 'LTX Video 2.0 I2V',
    technicalName: 'LTX Video 2.0 I2V',
    group: 'image-to-video',
    subtitleTags: ['Fast', 'Affordable', 'Audio'],
    tooltip: {
      description: 'Fast image-to-video with audio',
      capabilities: '✅ Image to Video (1 ref required), 🔊 Audio, 720p-1080p, 5s',
      pricing: '67 cr (720p) / 150 cr (1080p)',
      bestFor: 'Fast image animation with audio',
    },
  },
  'hailuo-02-i2v': {
    displayName: 'Hailuo-02 I2V',
    technicalName: 'Hailuo-02 I2V',
    group: 'image-to-video',
    subtitleTags: ['768p', '6s'],
    tooltip: {
      description: 'Simple image-to-video',
      capabilities: '✅ Image to Video (1 ref required), 768p, 6s',
      pricing: '90 cr',
      bestFor: 'Simple image animation',
    },
  },
  'kling-2.5-turbo-i2v': {
    displayName: 'Kling 2.5 Turbo I2V',
    technicalName: 'Kling 2.5 Turbo I2V',
    group: 'image-to-video',
    subtitleTags: ['Fast', '1080p'],
    tooltip: {
      description: 'Fast 1080p image-to-video',
      capabilities: '✅ Image to Video (1 ref required), 1080p, 5-10s',
      pricing: '117 cr (5s)',
      bestFor: 'Fast 1080p image animation',
    },
  },
  'sora-2-i2v': {
    displayName: 'Sora 2 I2V',
    technicalName: 'Sora 2 I2V',
    group: 'image-to-video',
    subtitleTags: ['Audio always on'],
    tooltip: {
      description: 'OpenAI image-to-video with built-in audio',
      capabilities: '✅ Image to Video (1 ref required), 🔊 Audio (always on), 4-10s',
      pricing: '133 cr (4s)',
      bestFor: 'Quality image animation with audio',
    },
  },
  'veo-3.1-fast-i2v': {
    displayName: 'Veo 3.1 Fast I2V',
    technicalName: 'Veo 3.1 Fast I2V',
    group: 'image-to-video',
    subtitleTags: ['Fast', 'Audio toggle'],
    tooltip: {
      description: 'Fast Google image-to-video with optional audio',
      capabilities: '✅ Image to Video (1 ref required), 🔊 Audio toggle, 4-8s',
      pricing: '133 cr (4s silent) / 200 cr (audio)',
      bestFor: 'Fast Google-quality image animation',
    },
  },
  'wan-2.2-i2v': {
    displayName: 'WAN 2.2 I2V',
    technicalName: 'WAN 2.2 I2V',
    group: 'image-to-video',
    subtitleTags: ['Commercial quality'],
    tooltip: {
      description: 'Commercial-quality image-to-video',
      capabilities: '✅ Image to Video (1 ref required), 720p, 5s',
      pricing: '133 cr (720p)',
      bestFor: 'Commercial-quality image animation',
    },
  },
  'kling-3.0-std-i2v': {
    displayName: 'Kling 3.0 Std I2V',
    technicalName: 'Kling 3.0 Std I2V',
    group: 'image-to-video',
    subtitleTags: ['Audio', '1080p', 'Optional end frame'],
    tooltip: {
      description: 'Image animation with audio, supports start+end frame',
      capabilities: '✅ Image to Video (1-2 refs), 🔊 Audio toggle, 1080p, 3-15s',
      pricing: '280 cr (5s silent) / 420 cr (audio)',
      bestFor: '1080p image animation with audio',
    },
  },
  'kling-o3-std-i2v': {
    displayName: 'Kling O3 Std I2V',
    technicalName: 'Kling O3 Std I2V',
    group: 'image-to-video',
    subtitleTags: ['Audio', '1080p'],
    tooltip: {
      description: 'Latest Kling image-to-video',
      capabilities: '✅ Image to Video (1-2 refs), 🔊 Audio toggle, 1080p, 5-10s',
      pricing: '280 cr (5s silent) / 373 cr (audio)',
      bestFor: 'Latest Kling image animation',
    },
  },
  'kling-3.0-pro-i2v': {
    displayName: 'Kling 3.0 Pro I2V',
    technicalName: 'Kling 3.0 Pro I2V',
    group: 'image-to-video',
    subtitleTags: ['Premium', 'Audio', '1080p'],
    tooltip: {
      description: 'Premium 1080p image-to-video with audio',
      capabilities: '✅ Image to Video (1-2 refs), 🔊 Audio toggle, 1080p, 5-10s',
      pricing: '373 cr (5s silent) / 560 cr (audio)',
      bestFor: 'Premium image animation',
    },
  },
  // Admin-only I2V
  'veo-3.1-i2v': {
    displayName: 'Veo 3.1 I2V',
    technicalName: 'Veo 3.1 I2V',
    group: 'image-to-video',
    subtitleTags: ['Audio', 'Admin only'],
    tooltip: {
      description: 'Google premium image-to-video with audio',
      capabilities: '✅ Image to Video (1 ref required), 🔊 Audio toggle, 4-8s',
      pricing: '267 cr (4s silent) / 533 cr (audio)',
      bestFor: 'Highest quality image animation',
    },
  },
  'sora-2-pro-i2v': {
    displayName: 'Sora 2 Pro I2V',
    technicalName: 'Sora 2 Pro I2V',
    group: 'image-to-video',
    subtitleTags: ['1080p', 'Admin only'],
    tooltip: {
      description: 'Premium OpenAI image-to-video',
      capabilities: '✅ Image to Video (1 ref required), 🔊 Audio, 1080p, 4-20s',
      pricing: '400 cr (4s 720p)',
      bestFor: 'Premium OpenAI image animation',
    },
  },
  'seedance-1.0-i2v': {
    displayName: 'Seedance 1.0 Pro I2V',
    technicalName: 'Seedance 1.0 Pro I2V',
    group: 'image-to-video',
    subtitleTags: ['Admin only'],
    tooltip: {
      description: 'Pro image animation',
      capabilities: '✅ Image to Video (1 ref required), 720p, 5s',
      pricing: '90 cr (5s 720p)',
      bestFor: 'Admin image animation',
    },
  },
  'seedance-1.0-fast-i2v': {
    displayName: 'Seedance 1.0 Fast I2V',
    technicalName: 'Seedance 1.0 Fast I2V',
    group: 'image-to-video',
    subtitleTags: ['Budget', 'Admin only'],
    tooltip: {
      description: 'Budget fast image animation',
      capabilities: '✅ Image to Video (1 ref required), 5s',
      pricing: '36 cr (5s)',
      bestFor: 'Budget image animation',
    },
  },
  'hailuo-02-pro-i2v': {
    displayName: 'Hailuo-02 Pro I2V',
    technicalName: 'Hailuo-02 Pro I2V',
    group: 'image-to-video',
    subtitleTags: ['1080p', 'Admin only'],
    tooltip: {
      description: '1080p Hailuo image animation',
      capabilities: '✅ Image to Video (1 ref required), 1080p, 6s',
      pricing: '160 cr (6s)',
      bestFor: 'Higher quality Hailuo animation',
    },
  },

  // ── Multi-Ref & Special ───────────────────────────────────────────────────
  'kling-o3-pro-ref': {
    displayName: 'Kling O3 Pro Reference',
    technicalName: 'Kling O3 Pro Reference',
    group: 'multi-ref-special',
    subtitleTags: ['Character consistency', '1-4 refs'],
    tooltip: {
      description: 'Character consistency in video using reference images',
      capabilities: '✅ Reference to Video (1-4 refs required), 🔊 Audio toggle, 1080p, 5-10s',
      pricing: '373 cr (5s silent) / 467 cr (audio)',
      bestFor: 'Character-consistent video generation',
    },
  },
  'veo-3.1-ref': {
    displayName: 'Veo 3.1 Reference',
    technicalName: 'Veo 3.1 Reference',
    group: 'multi-ref-special',
    subtitleTags: ['Multi-ref', 'Audio', 'Admin only'],
    tooltip: {
      description: 'Multi-reference Google video with audio',
      capabilities: '✅ Reference to Video (1-4 refs required), 🔊 Audio toggle, 4-8s',
      pricing: '533 cr (8s silent) / 1067 cr (audio)',
      bestFor: 'Multi-reference video with Google quality',
    },
  },
  'veo-3.1-flf': {
    displayName: 'Veo 3.1 First+Last',
    technicalName: 'Veo 3.1 First+Last',
    group: 'multi-ref-special',
    subtitleTags: ['Frame interp', 'Audio', 'Admin only'],
    tooltip: {
      description: 'Interpolate between two frames with audio',
      capabilities: '✅ First+Last Frame (2 refs required), 🔊 Audio toggle, 4-8s',
      pricing: '267 cr (4s silent) / 533 cr (audio)',
      bestFor: 'Frame interpolation, smooth transitions',
    },
  },
  'kling-2.1-pro-i2v': {
    displayName: 'Kling 2.1 Pro I2V',
    technicalName: 'Kling 2.1 Pro Multi-Image',
    group: 'multi-ref-special',
    subtitleTags: ['Multi-Image', 'Special effects', 'Admin only'],
    tooltip: {
      description: 'Multi-image video with special effects',
      capabilities: '✅ Multi-Image to Video (1-4 refs required), 5s',
      pricing: '163 cr (5s)',
      bestFor: 'Multi-image video generation',
    },
  },
};

// ─── Text Model Display Config ────────────────────────────────────────────────

export const TEXT_MODEL_DISPLAY: Record<string, ModelDisplayEntry> = {
  'gemini-3-flash-preview': {
    displayName: 'Nano Banana Text',
    technicalName: 'Gemini 3 Flash',
    group: 'text-generation',
    subtitleTags: ['Best quality', 'Latest'],
    tooltip: {
      description: 'Latest Google text generation model',
      capabilities: '✅ Text generation, ✅ System prompt',
      pricing: 'Free',
      bestFor: 'Best quality text generation',
    },
  },
  'gemini-2.0-flash': {
    displayName: 'Nano Banana Text Legacy',
    technicalName: 'Gemini 2.0 Flash',
    group: 'text-generation',
    subtitleTags: ['Fast', 'Reliable'],
    tooltip: {
      description: 'Fast and reliable text generation',
      capabilities: '✅ Text generation, ✅ System prompt',
      pricing: 'Free',
      bestFor: 'Fast reliable text generation',
    },
  },
};

// ─── Lookup Helper ────────────────────────────────────────────────────────────

/**
 * Get display config for any model ID. Falls back to using the API model name
 * if the model isn't in the config (future-proofs against new models added to
 * the backend before the frontend config is updated).
 */
export function getModelDisplay(
  modelId: string,
  mediaType: 'image' | 'video' | 'text',
  fallbackName?: string
): ModelDisplayEntry {
  const lookup =
    mediaType === 'video'
      ? VIDEO_MODEL_DISPLAY
      : mediaType === 'text'
        ? TEXT_MODEL_DISPLAY
        : IMAGE_MODEL_DISPLAY;

  const entry = lookup[modelId];
  if (entry) return entry;

  // Fallback for unknown models — show them in the first group with the API name
  const defaultGroup: CapabilityGroup =
    mediaType === 'video' ? 'text-to-video' : mediaType === 'text' ? 'text-generation' : 'text-to-image';

  return {
    displayName: fallbackName || modelId,
    technicalName: fallbackName || modelId,
    group: defaultGroup,
    subtitleTags: [],
    tooltip: {
      description: fallbackName || modelId,
      capabilities: '',
      pricing: '',
      bestFor: '',
    },
  };
}
